import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, message, Result, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import './LoginPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

function ResetPasswordPage() {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!token) {
      setTokenError('链接缺少 token，请重新申请密码重置');
      setVerifying(false);
      return;
    }
    axios
      .get(`${API_BASE}/auth/verify-reset-token`, { params: { token } })
      .then((res) => {
        if (!mounted) return;
        if (res.data?.success) {
          setTokenValid(true);
        } else {
          setTokenError(res.data?.error || '链接已失效');
        }
      })
      .catch((err) => {
        if (!mounted) return;
        setTokenError(err.response?.data?.error || '链接已失效');
      })
      .finally(() => mounted && setVerifying(false));
    return () => { mounted = false; };
  }, [token]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/reset-password`, {
        token,
        newPassword: values.newPassword
      });
      if (data.success) {
        setDone(true);
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      } else {
        message.error(data.error || '重置失败');
      }
    } catch (err) {
      message.error(err.response?.data?.error || '重置失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-container">
        <Card className="login-card" style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>正在验证链接...</div>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-container">
        <Card className="login-card">
          <Result
            status="error"
            title="链接已失效"
            subTitle={tokenError}
            extra={[
              <Link to="/forgot-password" key="retry">
                <Button type="primary">重新申请</Button>
              </Link>,
              <Link to="/login" key="login">
                <Button>返回登录</Button>
              </Link>
            ]}
          />
          <Footer className="card-footer" centered={true} />
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-container">
        <Card className="login-card">
          <Result
            status="success"
            title="密码已重置"
            subTitle="即将跳转到登录页..."
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">立即登录</Button>
              </Link>
            ]}
          />
          <Footer className="card-footer" centered={true} />
        </Card>
      </div>
    );
  }

  return (
    <div className="login-container">
      <Card className="login-card" title="设置新密码">
        <Form
          form={form}
          name="reset-password"
          onFinish={handleSubmit}
          size="large"
          className="login-form"
        >
          <Form.Item
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少 6 位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="新密码（至少 6 位）"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                }
              })
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="确认新密码"
              autoComplete="new-password"
              onPressEnter={() => form.submit()}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login-button"
              loading={submitting}
              block
            >
              提交并登录
            </Button>
          </Form.Item>
        </Form>

        <Footer className="card-footer" centered={true} />
      </Card>
    </div>
  );
}

export default ResetPasswordPage;
